/**
 * Conditional layout wrapper that applies the correct layout based on the route.
 * Auth pages use BlankLayout, protected pages use LayoutWrapper with VerticalLayout.
 */

// React Imports
import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

// Layout Imports
import LayoutWrapper from '@layouts/LayoutWrapper';
import VerticalLayout from '@layouts/VerticalLayout';
import BlankLayout from '@layouts/BlankLayout';
import Navbar from '@layouts/components/vertical/Navbar';
import Footer from '@layouts/components/vertical/Footer';
// Using relative path to avoid Vite path alias resolution issues
import { VerticalNav } from '../../@menu/components/VerticalNav';

// Type Imports
import type { SystemMode } from '@core/types';

// Utils
import { callbackPath } from '../../config/authConfig';
import { ModuleGuard } from '../common/ModuleGuard';

// MUI
import { Typography } from '@mui/material';

interface ConditionalLayoutProps {
  children: ReactNode;
  systemMode: SystemMode;
}

/**
 * Conditional layout component
 */
export const ConditionalLayout = ({ children, systemMode }: ConditionalLayoutProps) => {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, '') || '/';
  const callbackPathNorm = callbackPath.replace(/\/$/, '') || '/';

  // B2C callback page uses BlankLayout (loading spinner while SsoAuthHandler runs)
  if (path === callbackPathNorm) {
    return (
      <BlankLayout systemMode={systemMode}>
        {children}
      </BlankLayout>
    );
  }

  // Redirect to home if current route belongs to a disabled module
  const moduleGuard = <ModuleGuard />;

  // For protected routes, use LayoutWrapper with VerticalLayout
  const verticalLayout = (
    <VerticalLayout
      navigation={<VerticalNav />}
      navbar={<Navbar>{null}</Navbar>}
      footer={
        <Footer>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} CoOrdio Cortex. All rights reserved.
          </Typography>
        </Footer>
      }
    >
      {/* Children are injected by `LayoutWrapper` via `cloneElement`. */}
      {null}
    </VerticalLayout>
  );

  return (
    <>
      {moduleGuard}
      <LayoutWrapper systemMode={systemMode} verticalLayout={verticalLayout}>
        {children}
      </LayoutWrapper>
    </>
  );
};
